import React, { useMemo, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import DOMPurify from 'dompurify';
import { toPng } from 'html-to-image';
import { PostData } from '../../../models/Post';
import { PostLink } from '../../../models/ContentPlan';
import { ImageGenerationOverlay } from '../../ImageGenerationOverlay';
import { InlineEditableText } from './InlineEditableText';
import { LinksDisplay } from './PostLinksComponents';
import { CarouselCardsEditor } from './CarouselCardsEditor';
import { RichModalEditor, domPurifyConfig, ensureFontsLoaded, extractPrimaryFontsFromHtml } from './RichModalEditor';
import '../../../styles/carousel-fonts.css';

interface PostPreviewProps {
  post: PostData;
  onTitleChange: (newTitle: string) => void;
  onContentChange: (newContent: string) => void;
  onCarouselHtmlJsonChange: (newCarouselHtmlJson: string) => void;
  onCoverCardHtmlChange: (newCoverCardHtml: string) => void;
  onIdeaChange: (newIdea: string) => void;
  onLinksChange: (newLinks: PostLink[]) => void;
  onImageClick: () => void;
  onCoverDelete?: () => void;
  isGeneratingImage?: boolean;
  onReelsShotChange: (isShot: boolean) => void;
  onPostLinkChange: (postLink: string | undefined) => void;
}

export const PostPreview: React.FC<PostPreviewProps> = ({ 
  post, 
  onTitleChange, 
  onContentChange,
  onCarouselHtmlJsonChange,
  onCoverCardHtmlChange,
  onIdeaChange,
  onLinksChange: _onLinksChange,
  onImageClick,
  onCoverDelete: _onCoverDelete,
  isGeneratingImage,
  onReelsShotChange: _onReelsShotChange,
  onPostLinkChange: _onPostLinkChange
}) => {
  const [previewTab, setPreviewTab] = useState<'text' | 'carousel'>('text');

  const determinePostType = useMemo(() => {
    const format = (post.format || '').toLowerCase().trim();
    const platform = (post.platform || '').toLowerCase().trim();
    const russianFormatMap: Record<string, string> = {
      'рилс': 'reels', 'рил': 'reels', 'карусель': 'carousel', 'пост': 'post'
    };
    const normalizedFormat = russianFormatMap[format] || format;
    return {
      isReels: normalizedFormat === 'reels' || normalizedFormat === 'reel' || normalizedFormat.includes('reel'),
      isCarousel: normalizedFormat === 'carousel' || normalizedFormat.includes('carousel'),
      isRegularPost: normalizedFormat === 'post' || normalizedFormat.includes('post'),
      platform
    };
  }, [post.format, post.platform]);

  const sanitizedCoverCardHtml = useMemo(() => {
    const raw = post.coverCardHtml;
    if (!raw || !raw.trim()) return null;
    return DOMPurify.sanitize(raw, domPurifyConfig) as unknown as string;
  }, [post.coverCardHtml]);

  const [coverEditorOpen, setCoverEditorOpen] = useState(false);
  const [coverDownloading, setCoverDownloading] = useState(false);
  const [fontsReady, setFontsReady] = useState(false);
  const coverExportId = `reels-cover-export-${post.id || 'temp'}`;

  // Загружаем шрифты для обложки Reels и карусели
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const families = [];
        if (sanitizedCoverCardHtml) {
          families.push(...extractPrimaryFontsFromHtml(sanitizedCoverCardHtml));
        }
        if (post.carouselHtmlJson) {
          try {
            const cards = JSON.parse(post.carouselHtmlJson);
            if (Array.isArray(cards)) {
              cards.forEach((c: any) => {
                if (typeof c === 'string') {
                  families.push(...extractPrimaryFontsFromHtml(c));
                }
              });
            }
          } catch (e) {
            // ignore
          }
        }

        if (families.length > 0) {
          await ensureFontsLoaded(Array.from(new Set(families)));
        }
      } catch (e) {
        console.error('Error loading fonts for post preview', e);
      } finally {
        if (!cancelled) setFontsReady(true);
      }
    };
    setFontsReady(false);
    void run();
    return () => { cancelled = true; };
  }, [sanitizedCoverCardHtml, post.carouselHtmlJson]);

  const handleDownloadCover = async () => {
    if (coverDownloading) return;
    setCoverDownloading(true);
    try {
      const el = document.getElementById(coverExportId);
      if (el instanceof HTMLElement) {
        const dataUrl = await toPng(el, { width: 1080, height: 1920 });
        const link = document.createElement('a');
        link.download = 'reels-cover.png'; link.href = dataUrl; link.click();
      }
    } catch (e) { console.error(e); } finally { setCoverDownloading(false); }
  };

  const styles = useMemo(() => {
    const platform = (post.platform || '').toLowerCase().trim();
    const result = {
      containerClasses: "bg-white dark:bg-gray-800 rounded-lg shadow-md mx-auto relative flex flex-col min-h-full w-full",
      coverContainerClasses: "relative cursor-pointer",
      titleClasses: "font-semibold text-gray-900 dark:text-white",
      contentClasses: "whitespace-pre-wrap text-gray-800 dark:text-gray-200 text-sm",
    };

    if (platform === 'instagram') {
      if (determinePostType.isReels) {
        result.titleClasses = "font-semibold text-gray-900 dark:text-white text-base mb-1";
      } else if (determinePostType.isCarousel || determinePostType.isRegularPost) {
        result.containerClasses += " md:max-w-xs";
        result.coverContainerClasses += " aspect-square w-full";
      }
    } else if (platform === 'telegram') {
      result.containerClasses += " md:max-w-sm";
      result.coverContainerClasses += " aspect-[16/9] w-full";
      result.titleClasses = "font-bold text-lg mb-2 text-gray-900 dark:text-white";
    }
    
    if (determinePostType.isCarousel && previewTab === 'carousel') {
      result.containerClasses = result.containerClasses.replace("md:max-w-xs", "md:max-w-none");
    }

    return result;
  }, [post.platform, determinePostType, previewTab]);

  return (
    <div className={styles.containerClasses}>
      {!(determinePostType.isCarousel && previewTab === 'carousel') && (
        <div className="p-4 border-b border-blue-100 dark:border-blue-800">
          <div className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-1">Идея:</div>
          <InlineEditableText value={post.idea} onChange={onIdeaChange} className="text-sm text-blue-800 dark:text-blue-300" />
        </div>
      )}

      <div className="p-4">
        {determinePostType.isReels && sanitizedCoverCardHtml && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-gray-600">Обложка Reels:</div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setCoverEditorOpen(true)} className="px-3 py-1.5 text-xs rounded-md border border-gray-300">Редактировать</button>
                <button type="button" onClick={handleDownloadCover} className="px-3 py-1.5 text-xs rounded-md bg-blue-600 text-white">{coverDownloading ? '…' : 'Скачать'}</button>
              </div>
            </div>
            <div className="relative w-full max-w-[220px] aspect-[9/16] overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div 
                style={{ 
                  width: 1080, 
                  height: 1920, 
                  transform: 'scale(0.2037)', 
                  transformOrigin: 'top left',
                  visibility: fontsReady ? 'visible' : 'hidden'
                }} 
                dangerouslySetInnerHTML={{ __html: sanitizedCoverCardHtml }} 
              />
              {!fontsReady && (
                <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-500">
                  Загружаем шрифты…
                </div>
              )}
            </div>
            <div id={coverExportId} className="fixed left-[-10000px] top-0 opacity-0 pointer-events-none" aria-hidden="true">
              <div 
                style={{ 
                  width: 1080, 
                  height: 1920,
                  visibility: fontsReady ? 'visible' : 'hidden'
                }} 
                dangerouslySetInnerHTML={{ __html: sanitizedCoverCardHtml }} 
              />
            </div>
          </div>
        )}

        {!(determinePostType.isCarousel && previewTab === 'carousel') && (
          <InlineEditableText value={post.title} onChange={onTitleChange} className={styles.titleClasses} isTitle />
        )}

        {determinePostType.isCarousel ? (
          previewTab === 'carousel' ? (
            <CarouselCardsEditor carouselHtmlJson={post.carouselHtmlJson} onChange={onCarouselHtmlJsonChange} />
          ) : (
            <div className="space-y-2">
              {(() => {
                try {
                  const carouselContent = JSON.parse(post.content);
                  if (!Array.isArray(carouselContent)) {
                    return <InlineEditableText value={post.content} onChange={onContentChange} className={styles.contentClasses} />;
                  }
                  return carouselContent.map((item, index) => {
                    // Преобразуем элемент в строку, если это объект
                    let itemText: string;
                    if (typeof item === 'string') {
                      itemText = item;
                    } else if (typeof item === 'object' && item !== null) {
                      // Если объект имеет структуру {slide, header, text, visual}, извлекаем text или объединяем поля
                      if ('text' in item && typeof item.text === 'string') {
                        itemText = item.text;
                      } else if ('header' in item && typeof item.header === 'string') {
                        itemText = item.header;
                      } else {
                        // Пытаемся преобразовать объект в строку
                        itemText = JSON.stringify(item);
                      }
                    } else {
                      itemText = String(item);
                    }
                    
                    return (
                      <div key={index} className="bg-gray-100 dark:bg-gray-700 p-2 rounded">
                        <div className="text-xs font-semibold mb-1">Слайд {index + 1}:</div>
                        <InlineEditableText 
                          value={itemText} 
                          onChange={txt => {
                            const next = [...carouselContent];
                            // Сохраняем как строку, если исходный элемент был строкой, иначе обновляем объект
                            if (typeof item === 'string') {
                              next[index] = txt;
                            } else if (typeof item === 'object' && item !== null && 'text' in item) {
                              next[index] = { ...item, text: txt };
                            } else {
                              next[index] = txt;
                            }
                            onContentChange(JSON.stringify(next));
                          }} 
                          className={styles.contentClasses} 
                        />
                      </div>
                    );
                  });
                } catch {
                  return <InlineEditableText value={post.content} onChange={onContentChange} className={styles.contentClasses} />;
                }
              })()}
            </div>
          )
        ) : (
          <InlineEditableText value={post.content} onChange={onContentChange} className={styles.contentClasses} />
        )}
        
        <LinksDisplay links={post.links || []} className="mt-3" />
      </div>

      {determinePostType.isCarousel && (
        <div className="sticky bottom-2 left-0 right-0 flex justify-center z-20 mt-auto p-4 pointer-events-none">
          <div className="flex items-center gap-4 pointer-events-auto bg-white/80 dark:bg-gray-800/80 p-1 rounded-2xl backdrop-blur shadow-lg">
            <button type="button" onClick={() => setPreviewTab('text')} className={`px-10 py-2 text-sm font-bold rounded-2xl transition-all ${previewTab === 'text' ? 'bg-blue-600 text-white' : 'text-gray-600'}`}>Текст</button>
            <button type="button" onClick={() => setPreviewTab('carousel')} className={`px-10 py-2 text-sm font-bold rounded-2xl transition-all ${previewTab === 'carousel' ? 'bg-blue-600 text-white' : 'text-gray-600'}`}>Карусели</button>
          </div>
        </div>
      )}

      {coverEditorOpen && (
        <RichModalEditor open={coverEditorOpen} html={post.coverCardHtml || ''} isCover onClose={() => setCoverEditorOpen(false)} onSave={html => { onCoverCardHtmlChange(html); setCoverEditorOpen(false); }} />
      )}
    </div>
  );
};

